import React, { ReactNode, useState } from "react";
import {
  TableBody,
  TableContainer,
  TableRow,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
} from "@mui/material";
import {
  StyledTable,
  StyledTableCell,
  StyledTableHeader,
  StyledTableRow,
  StyledPinnedCell,
  StyledRowActionsWrapperBox,
  StyledIconButton,
} from "@/components/styled";
import { tableCellClasses } from "@mui/material";
import SortableTableHeader from "@/components/SortableTableHeader";

export interface DashboardManagementTableColumn {
  label: string;
  field: string;
  render?: (value: any, row: any) => ReactNode;
  sortable?: boolean;
}

export interface DashboardManagementTableAction {
  icon: ReactNode;
  onClick: (row: any) => void;
  variant?: "text" | "outlined" | "dangerOutlined";
  label?: string;
}

interface DashboardManagementTableProps {
  columns: DashboardManagementTableColumn[];
  rows: any[];
  pagination: ReactNode;
  actions?: DashboardManagementTableAction[];
  showActionsColumn?: boolean;
}

const DashboardManagementTable: React.FC<DashboardManagementTableProps> = ({
  columns,
  rows,
  pagination,
  actions = [],
  showActionsColumn = false,
}) => {
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(
    null
  );

  const handleSort = (field: string) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField("");
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortDirection = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection;
  };

  const sortedRows = React.useMemo(() => {
    if (!sortField || !sortDirection) return rows;

    return [...rows].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      // Handle numeric values
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      const aString = String(aValue || "").toLowerCase();
      const bString = String(bValue || "").toLowerCase();

      if (sortDirection === "asc") {
        return aString.localeCompare(bString);
      } else {
        return bString.localeCompare(aString);
      }
    });
  }, [rows, sortField, sortDirection]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <TableContainer
        sx={{ flex: 1, overflow: "auto", maxHeight: "calc(100% - 80px)" }}
      >
        <StyledTable aria-label="customized table">
          <StyledTableHeader>
            <TableRow>
              {columns.map((col) => (
                <StyledTableCell key={col.field}>
                  {col.sortable ? (
                    <SortableTableHeader
                      label={col.label}
                      sortDirection={getSortDirection(col.field)}
                      onSort={() => handleSort(col.field)}
                    />
                  ) : (
                    <Typography variant="body2">{col.label}</Typography>
                  )}
                </StyledTableCell>
              ))}
              {showActionsColumn && actions.length > 0 && (
                <StyledTableCell sx={{ minWidth: "150px" }}></StyledTableCell>
              )}
            </TableRow>
          </StyledTableHeader>
          <TableBody>
            {sortedRows.length === 0 ? (
              <StyledTableRow>
                <StyledTableCell
                  colSpan={columns.length + (showActionsColumn ? 1 : 0)}
                  align="center"
                >
                  No data
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              sortedRows.map((row, idx) => (
                <StyledTableRow key={idx}>
                  {columns.map((col, colIdx) => (
                    <StyledTableCell key={colIdx}>
                      {col.render
                        ? col.render(row[col.field], row)
                        : row[col.field] !== undefined
                        ? row[col.field]
                        : "-"}
                    </StyledTableCell>
                  ))}
                  {showActionsColumn && actions.length > 0 && (
                    <StyledPinnedCell
                      sx={{
                        [`&.${tableCellClasses.body}`]: { paddingTop: "20px" },
                      }}
                    >
                      <StyledRowActionsWrapperBox>
                        {actions.map((action, actionIdx) => (
                          <StyledIconButton
                            key={actionIdx}
                            variant={action.variant || "text"}
                            onClick={() => action.onClick(row)}
                            sx={downSm && action.label ? { flex: 1 } : {}}
                          >
                            {downSm && action.label
                              ? action.label
                              : action.icon}
                          </StyledIconButton>
                        ))}
                      </StyledRowActionsWrapperBox>
                    </StyledPinnedCell>
                  )}
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <Box sx={{ flexShrink: 0 }}>{pagination}</Box>
    </Box>
  );
};

export default DashboardManagementTable;
